<script setup>
import { Link } from '@inertiajs/vue3'
import ClickToActionSection from '@/components/ClickToActionSection.vue'
import { FireIcon, BoltIcon, CubeIcon } from '@heroicons/vue/24/solid'
import { index as contactIndex } from '@/actions/App/Http/Controllers/ContactController'
import { index as projectIndex } from '@/actions/App/Http/Controllers/ProjectController'
import ProjectCard from '@/pages/projects/partials/ProjectCard.vue'
import ServiceCard from '@/pages/services/Partials/ServiceCard.vue'

defineProps({
    projects: {
        type: Array,
        required: true,
    },

    serviceName: {
        type: String,
        required: true,
    },
})

const seo = {
    title: 'Výměna kotlů',
    description:
        'Výměna a instalace plynových kotlů, elektrokotlů a kotlů na pevná paliva. Odborný návrh, montáž, revize a servis. Spolehlivé řešení vytápění od firmy Kalianko.',
}

const services = [
    {
        name: 'Plynové kotle',
        description: 'Efektivní a komfortní řešení pro moderní vytápění.',
        icon: FireIcon,
        features: [
            'Zaměření a návrh vhodného řešení',
            'Zpracování cenové nabídky',
            'Zajištění nového odvodu spalinových cest',
            'Odborná instalace a uvedení do provozu',
            'Proplach topných systémů',
            'Napuštění systému chemicky upravenou vodou',
            'Zajištění revizí plynu a spalinových cest',
            'Pravidelný servis a údržba',
        ],
    },
    {
        name: 'Elektrokotle',
        description: 'Jednoduché a spolehlivé vytápění bez nutnosti plynové přípojky.',
        icon: BoltIcon,
        features: [
            'Posouzení vhodnosti řešení pro objekt',
            'Návrh a cenová nabídka',
            'Instalace a zapojení do systému',
            'Servis a kontrola funkčnosti',
            'Odborné poradenství',
        ],
    },
    {
        name: 'Kotle na pevná paliva',
        description: 'Tradiční a ekonomické řešení s důrazem na výkon a úsporu.',
        icon: CubeIcon,
        features: [
            'Návrh optimálního typu kotle',
            'Zpracování nabídky',
            'Instalace včetně napojení na systém',
            'Zajištění revizí spalinových cest',
            'Servis a údržba',
        ],
    },
]

const benefits = [
    { value: '24h', label: 'Havarijní služba' },
    { value: '30+', label: 'Let zkušeností' },
    { value: '100%', label: 'Záruka na práci' },
]
</script>

<template>
    <Head :title="seo.title">
        <meta
            name="description"
            :content="seo.description"
        />
    </Head>

    <section>
        <!-- Hero Section -->
        <div class="relative isolate overflow-hidden bg-gray-900">
            <img
                src="/images/kalianko-team.png"
                alt="Výměna kotlů - profesionální instalace a výměna kotlů"
                class="absolute inset-0 -z-10 size-full object-cover opacity-20"
            />
            <div class="absolute inset-0 -z-10 bg-linear-to-b from-gray-900/50 via-gray-900/80 to-gray-900" />

            <div class="mx-auto max-w-4xl px-6 py-32 text-center sm:py-40 lg:px-8 lg:py-56">
                <h1 class="text-4xl font-bold tracking-tight text-white sm:text-6xl">Výměna kotlů</h1>

                <p class="mx-auto mt-6 max-w-2xl text-lg/8 text-gray-300">
                    Odborná výměna a instalace kotlů všech typů. Od plynových a elektrických kotlů po kotle na pevná
                    paliva. Zajistíme kompletní servis od návrhu po uvedení do provozu.
                </p>

                <div class="mt-10 flex flex-col items-center justify-center gap-2 lg:flex-row">
                    <Link
                        :href="contactIndex().url + '#demand-form'"
                        class="primary-link inline-flex w-full items-center justify-center rounded-full text-lg lg:w-fit"
                    >
                        Nezávazná poptávka
                    </Link>
                    <Link
                        :href="projectIndex().url + '?service=' + serviceName"
                        class="inline-flex w-full items-center justify-center rounded-full border border-white/30 bg-white/10 px-6 py-3 text-lg font-semibold text-white backdrop-blur-lg transition hover:bg-white/20 sm:w-auto"
                    >
                        Naše realizace
                    </Link>
                </div>

                <!-- Quick benefits -->
                <div class="mt-16 grid grid-cols-3 gap-4 border-t border-white/10 pt-10">
                    <div
                        v-for="benefit in benefits"
                        :key="benefit.label"
                        class="text-center"
                    >
                        <div class="text-brand-primary text-3xl font-bold">{{ benefit.value }}</div>
                        <div class="mt-1 text-sm text-gray-400">{{ benefit.label }}</div>
                    </div>
                </div>
            </div>
        </div>

        <!-- Features Section -->
        <div class="section-width space-y-20 px-2 py-24 sm:py-32 lg:px-0">
            <div class="mx-auto max-w-2xl text-center">
                <p class="text-brand-primary text-sm font-semibold tracking-wider uppercase">Naše služby</p>
                <h2 class="heading-2 mt-2">Co pro vás můžeme udělat</h2>
                <p class="paragraph mt-4">
                    Nabízíme kompletní řešení výměny a instalace kotlů. Pomůžeme vám vybrat optimální typ kotle s
                    ohledem na váš objekt, energetickou náročnost i rozpočet. Zajistíme vše od návrhu přes montáž až po
                    revize a následný servis.
                </p>
            </div>

            <div class="mt-16 grid grid-cols-1 gap-8 lg:grid-cols-3">
                <ServiceCard
                    v-for="service in services"
                    :key="service.name"
                    :service="service"
                />
            </div>

            <ClickToActionSection />

            <div
                v-if="projects.length > 0"
                class="space-y-5"
            >
                <div class="mx-auto max-w-2xl lg:mx-0">
                    <h2 class="heading-2">Projekt z výměny kotlů</h2>
                    <p class="paragraph mt-4">
                        Podívejte se na některé z našich nedávných realizací v oblasti výměny kotlů. Každý projekt je
                        pro nás výzvou k předvedení našich schopností a odbornosti.
                    </p>
                </div>
                <div class="grid auto-rows-fr grid-cols-1 gap-8 lg:grid-cols-3">
                    <div
                        v-for="project in projects"
                        :key="project.id"
                        class="group"
                    >
                        <ProjectCard :project="project" />
                    </div>
                </div>
            </div>
        </div>
    </section>
</template>
